import React from 'react';
import { RefreshCw } from 'lucide-react';
import { FileCardBase } from '../fileProcessing/FileCardBase';
import SimpleEditableField from './SimpleEditableField';
import SimpleKeywordsManager from './SimpleKeywordsManager';

interface CSVRow {
  filename: string;
  title: string;
  description: string;
  keywords: string;
  // Allow arbitrary additional fields
  [key: string]: string;
}

interface CSVCardProps {
  row: CSVRow;
  index: number;
  isRegenerating: boolean;
  isCheckingCreditsForIndividual: boolean;
  onRegenerate: (index: number) => void;
  onUpdate: (index: number, field: keyof CSVRow, value: string) => void;
}

const CSVCard: React.FC<CSVCardProps> = ({
  row,
  index,
  isRegenerating,
  isCheckingCreditsForIndividual,
  onRegenerate,
  onUpdate
}) => {
  // Create dummy file data for FileCardBase since CSV doesn't have real files
  const dummyFile = {
    id: `csv-${index}`,
    name: row.filename,
    size: '0 B',
    fileSize: 0,
    type: 'text/csv',
    lastModified: Date.now(),
    status: 'complete' as const,
    metadata: {
      title: row.title,
      description: row.description,
      keywords: row.keywords ? row.keywords.split(',').map(k => k.trim()).filter(k => k) : []
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  const handleRegenerate = () => {
    onRegenerate(index);
  };

  const handleFieldChange = (field: string, value: string) => {
    onUpdate(index, field, value);
  };

  // Truncate filename for display (frontend only - DB keeps original)
  const truncateFilename = (filename: string, maxLength: number = 28) => {
    if (filename.length <= maxLength) return filename;

    // Find the last dot for extension
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex === -1) {
      // No extension, just truncate
      return filename.slice(0, maxLength - 3) + '...';
    }

    const extension = filename.slice(lastDotIndex);
    const nameWithoutExt = filename.slice(0, lastDotIndex);

    // Calculate available space for name (total - extension - ellipsis)
    const availableLength = maxLength - extension.length - 3;

    if (availableLength < 1) {
      // Extension too long, just show first part
      return filename.slice(0, maxLength - 3) + '...';
    }

    return nameWithoutExt.slice(0, availableLength) + '...' + extension;
  };

  return (
    <FileCardBase file={dummyFile} className="p-4 md:p-6 bg-green-50 border-green-200">
      <div className="space-y-4 relative">
        {/* Status and Regenerate Button */}
        <div className="flex justify-between items-start">
          <div className="flex items-center text-xs text-gray-500">
            <span>CSV Row #{index + 1}</span>
            <span className="mx-2">•</span>
            <div className="flex items-center text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
              Ready
            </div>
          </div>

          <button
            onClick={handleRegenerate}
            disabled={isRegenerating || isCheckingCreditsForIndividual}
            className={`flex items-center px-3 py-2 text-sm rounded-lg ${
              isRegenerating || isCheckingCreditsForIndividual
                ? 'bg-gray-100 border border-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gray-50 border border-gray-300 text-gray-700 hover:bg-gray-100'
            }`}
            title={
              isRegenerating
                ? 'Regenerating metadata...'
                : isCheckingCreditsForIndividual
                ? 'Checking credits...'
                : 'Re-generate metadata (costs 1 credit)'
            }
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${
              isRegenerating || isCheckingCreditsForIndividual ? 'animate-spin' : ''
            }`} />
            {isRegenerating
              ? 'Regenerating...'
              : isCheckingCreditsForIndividual
              ? 'Checking...'
              : 'Re-generate metadata'
            }
          </button>
        </div>

        {/* Loading message when regenerating */}
        {isRegenerating && (
          <div className="text-xs text-blue-600 text-center">
            <div>🤖 AI generating new metadata...</div>
          </div>
        )}

        {/* Skeleton Loading Overlay */}
        {isRegenerating && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg z-10 flex items-center justify-center">
            <div className="text-center space-y-3 p-6">
              <div className="inline-block">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">AI is crafting new metadata...</p>
                <p className="text-xs text-gray-500">This will take a few seconds</p>
              </div>
            </div>
          </div>
        )}

        {/* Metadata Fields */}
        {/* Filename */}
        <div className="space-y-2">
          <SimpleEditableField
            label="Filename"
            value={row.filename}
            fieldType="filename"
            fileId={`csv-${index}`}
            onSave={async (_fileId: string, updatedMetadata: { [key: string]: string }) => {
              if (updatedMetadata.filename !== undefined) {
                handleFieldChange('filename', updatedMetadata.filename);
              }
            }}
            maxLength={255}
            placeholder="Enter filename..."
            disabled={isRegenerating}
          />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <SimpleEditableField
            label="Title"
            value={row.title}
            fieldType="title"
            fileId={`csv-${index}`}
            onSave={async (_fileId: string, updatedMetadata: { [key: string]: string }) => {
              if (updatedMetadata.title !== undefined) {
                handleFieldChange('title', updatedMetadata.title);
              }
            }}
            maxLength={150}
            placeholder="Enter a descriptive title..."
            disabled={isRegenerating}
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <SimpleEditableField
            label="Description"
            value={row.description}
            fieldType="description"
            fileId={`csv-${index}`}
            onSave={async (_fileId: string, updatedMetadata: { [key: string]: string }) => {
              if (updatedMetadata.description !== undefined) {
                handleFieldChange('description', updatedMetadata.description);
              }
            }}
            maxLength={300}
            placeholder="Enter a detailed description..."
            disabled={isRegenerating}
          />
        </div>

        {/* Keywords with Drag & Drop */}
        <SimpleKeywordsManager
          keywords={row.keywords ? row.keywords.split(',').map(k => k.trim()).filter(k => k) : []}
          fileId={`csv-${index}`}
          onSave={async (_fileId: string, metadata: { keywords: string[] }) => {
            if (metadata.keywords) {
              handleFieldChange('keywords', metadata.keywords.join(', '));
            }
          }}
          disabled={isRegenerating}
          maxKeywords={100}
          maxKeywordLength={80}
        />

        {/* Additional/Custom Fields */}
        {Object.keys(row).map(key => {
          // Skip standard fields that are already displayed above
          // Use case-insensitive comparison to handle different CSV column naming
          const keyLower = key.toLowerCase().trim();
          if (['filename', 'title', 'description', 'keywords'].includes(keyLower)) {
            return null;
          }

          return (
            <div key={key} className="flex items-center gap-2 py-1">
              <label className="text-sm font-medium text-gray-700 min-w-0 flex-shrink-0">
                {key.charAt(0).toUpperCase() + key.slice(1)}:
              </label>
              <div className="flex-1">
                <SimpleEditableField
                  label="" // No label since we show it outside
                  value={row[key] || ''}
                  fieldType={key} // Use the actual field name as fieldType for custom fields
                  fileId={`csv-${index}-${key}`}
                  onSave={async (_fileId: string, updatedMetadata: { [key: string]: string }) => {
                    if (updatedMetadata[key] !== undefined) {
                      handleFieldChange(key, updatedMetadata[key]);
                    }
                  }}
                  maxLength={500} // Default max length for additional fields
                  placeholder={`Enter ${key}...`}
                  disabled={isRegenerating}
                />
              </div>
            </div>
          );
        })}
      </div>
    </FileCardBase>
  );
};

export default CSVCard;
